import * as v from 'valibot'

/** Position: [lon, lat] or [lon, lat, alt] */
const Position2D = v.tuple([v.number(), v.number()])
const Position3D = v.tuple([v.number(), v.number(), v.number()])
const Position = v.union([Position2D, Position3D])

/** bbox: 2D (4) or 3D (6) */
const BBox2D = v.tuple([v.number(), v.number(), v.number(), v.number()])
const BBox3D = v.tuple([v.number(), v.number(), v.number(), v.number(), v.number(), v.number()])
const BBox = v.union([BBox2D, BBox3D])

/** Reusable ring/line helpers (min sizes per GeoJSON spec) */
const Line = v.pipe(v.array(Position), v.minLength(2)) // LineString: >=2 positions
const LinearRing = v.pipe(v.array(Position), v.minLength(4)) // Polygon ring: >=4 positions (closure not enforced)

/** Geometry schemas (defined first without recursion) */
export const Point = v.object({
	type: v.literal('Point'),
	coordinates: Position,
	bbox: v.optional(BBox),
})

export const MultiPoint = v.object({
	type: v.literal('MultiPoint'),
	coordinates: v.pipe(v.array(Position), v.minLength(1)),
	bbox: v.optional(BBox),
})

export const LineString = v.object({
	type: v.literal('LineString'),
	coordinates: Line,
	bbox: v.optional(BBox),
})

export const MultiLineString = v.object({
	type: v.literal('MultiLineString'),
	coordinates: v.pipe(v.array(Line), v.minLength(1)),
	bbox: v.optional(BBox),
})

export const Polygon = v.object({
	type: v.literal('Polygon'),
	coordinates: v.pipe(v.array(LinearRing), v.minLength(1)),
	bbox: v.optional(BBox),
})

export const MultiPolygon = v.object({
	type: v.literal('MultiPolygon'),
	coordinates: v.pipe(v.array(v.pipe(v.array(LinearRing), v.minLength(1))), v.minLength(1)),
	bbox: v.optional(BBox),
})

export type TGeometry =
	| v.InferOutput<typeof Point>
	| v.InferOutput<typeof MultiPoint>
	| v.InferOutput<typeof LineString>
	| v.InferOutput<typeof MultiLineString>
	| v.InferOutput<typeof Polygon>
	| v.InferOutput<typeof MultiPolygon>
	| {
			type: string
			geometries: TGeometry[]
			bbox?: v.InferOutput<typeof BBox>
	  }

/** GeometryCollection (recursive) */
export const Geometry: v.GenericSchema<TGeometry> = v.lazy(() =>
	v.union([
		Point,
		MultiPoint,
		LineString,
		MultiLineString,
		Polygon,
		MultiPolygon,
		v.object({
			type: v.literal('GeometryCollection'),
			geometries: v.array(Geometry),
			bbox: v.optional(BBox),
		}),
	]),
)

/** Feature */
export const Feature = v.object({
	type: v.literal('Feature'),
	geometry: v.union([Geometry, v.null()]),
	properties: v.union([v.record(v.string(), v.unknown()), v.null()]),
	id: v.optional(v.union([v.string(), v.number()])),
	bbox: v.optional(BBox),
})

/** FeatureCollection */
export const FeatureCollection = v.object({
	type: v.literal('FeatureCollection'),
	features: v.pipe(v.array(Feature), v.minLength(1)),
	bbox: v.optional(BBox),
})

const geojson_schemas = {
	line: Line,
	line_string: LineString,
	polygon: Polygon,
	multi_polygon: MultiPolygon,
	geometry: Geometry,
	feature: Feature,
	feature_collection: FeatureCollection,
}

export default geojson_schemas
